import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Users, Shield, Clock, Award, Handshake, Zap } from 'lucide-react';
import { whyChooseUs } from '../data/mock';

gsap.registerPlugin(ScrollTrigger);

const iconMap = {
  Users: Users,
  Shield: Shield,
  Clock: Clock,
  Award: Award,
  Handshake: Handshake,
  Zap: Zap,
};

// Homepage variant: 4 items in one row (excludes Long-Term Relationships and Specialist Capability)
const homePageItems = whyChooseUs.filter(item => 
  item.title !== "Long-Term Relationships" && item.title !== "Specialist Capability"
);

const WhyChooseUs = ({ variant = "default" }) => {
  const sectionRef = useRef(null);
  const cardsRef = useRef([]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Section title animation
      gsap.fromTo(
        '.why-title',
        { y: 50, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      // Cards stagger animation
      cardsRef.current.forEach((card, index) => {
        gsap.fromTo(
          card,
          { y: 60, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.6,
            delay: index * 0.1,
            scrollTrigger: {
              trigger: card,
              start: 'top 85%',
              toggleActions: 'play none none reverse',
            },
          }
        );
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  // Use homepage items (4) or all items (6) based on variant
  const items = variant === "homepage" ? homePageItems : whyChooseUs;
  const gridCols = variant === "homepage" 
    ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-4" 
    : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3";

  return (
    <section ref={sectionRef} className="py-24 bg-white relative overflow-hidden">
      {/* Background Accent */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-securelec-red/5 rounded-full blur-[150px]" />

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="why-title inline-block text-securelec-red text-sm font-semibold tracking-widest uppercase mb-4">
            Why Choose Us
          </span>
          <h2 className="why-title text-4xl md:text-5xl font-bold text-securelec-dark mb-6">
            The Securelec <span className="text-securelec-red">Difference</span>
          </h2>
          <div className="why-title w-24 h-1 bg-gradient-to-r from-transparent via-securelec-red to-transparent mx-auto" />
        </div>

        {/* Cards Grid */}
        <div className={`grid ${gridCols} gap-6`}>
          {items.map((item, index) => {
            const IconComponent = iconMap[item.icon];
            return (
              <div
                key={item.id}
                ref={(el) => (cardsRef.current[index] = el)}
                className="group bg-white border border-gray-200 rounded-xl p-8 hover:border-securelec-red/50 hover:shadow-xl transition-all duration-500 relative overflow-hidden"
              >
                {/* Hover Background Effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-securelec-red/0 to-securelec-red/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                {/* Icon */}
                <div className="relative z-10 mb-6">
                  <div className="w-14 h-14 bg-securelec-red/10 rounded-xl flex items-center justify-center group-hover:bg-securelec-red/20 transition-colors duration-300">
                    <IconComponent className="w-7 h-7 text-securelec-red group-hover:scale-110 transition-transform duration-300" />
                  </div>
                </div>

                {/* Content */}
                <h3 className="relative z-10 text-xl font-bold text-securelec-dark mb-3 group-hover:text-securelec-red transition-colors duration-300">
                  {item.title}
                </h3>
                <p className="relative z-10 text-securelec-gray/70 leading-relaxed">
                  {item.description}
                </p>

                {/* Bottom Accent Line */}
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-securelec-red to-transparent scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
